import { Box, Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@progress/kendo-react-buttons';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { ApplicationState } from '../../../store';
import {
  dateFormat,
  flattenNodeConfigData,
  getLocalDateTime,
} from '../../../utils/utils';
import { LocaleInterface, locales } from '../../AlertsGrid/GridLocale';
import StringResourceManager, { GetString } from '../../StringResourceManager';
// Interfaces
import { GridRowAssetTableInfo } from '../../../Interfaces/Assets/AssetInfo';
import {
  clearEditConfigSettings,
  editConfigSettings,
  getConfigSettingsByNodeUuid,
  resetFetchError,
} from '../../../store/Config/Settings/ConfigSettingsActions';

import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { ApiEnums } from '../../../Interfaces/ApiEnums';
import {
  ConfigSetting,
  EditConfigSetting,
  UpdateConfigSetting,
} from '../../../Interfaces/Config/ConfigSetting';
import { NodeConfigInfo } from '../../../Interfaces/Config/NodeConfigInfo';
import { FetchError } from '../../../Interfaces/FetchError';
import ApplyScheduleDatePickerDialog from './ApplyScheduleDatePickerDialog';
import JsonNodeConfigDetailComponent from './JsonNodeConfigDetailComponent';
import NodeConfigDetailComponent from './NodeConfigDetailComponent';

const useStyles = makeStyles(theme => ({
  gridAlign: {
    display: 'flex',
    width: '100%',
    maxWidth: '100%',
  },
  margin: {
    margin: theme.spacing(1),
  },
  textField: {
    width: '25ch',
  },
  box: {
    height: 60,
    display: 'flex',
    padding: theme.spacing(1),
    margin: theme.spacing(1),
  },
  spreadBox: {
    alignItems: 'flex-start',
  },
  dialogAction: {
    height: '3rem',
    padding: theme.spacing(3),
    marginRight: theme.spacing(1),
  },
  dialogLabel: {
    fontSize: '1.1rem',
    display: 'inline',
  },
  dialogTextBold: {
    fontWeight: 'bold',
  },
  bottomRight: {
    right: 0,
    bottom: 0,
    alignItems: 'flex-start',
    flexWrap: 'wrap-reverse',
  },
  send: {
    fontSize: '1.5rem',
  },
  error: {
    fontSize: '1.5rem',
  },
}));
interface Props {
  nodeConfigData: NodeConfigInfo;
  configSettingType: ApiEnums.ConfigSettingsType;
  closePage: () => void;
}

const NodeAssetsConfigForm: React.FC<Props> = ({
  nodeConfigData,
  configSettingType,
  closePage,
}) => {
  const classes = useStyles();

  const openBBoxRef = React.useRef<() => void>();

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const dispatch: Dispatch<any> = useDispatch();

  const [
    isApplyScheduleDatePickerDialogVisible,
    setIsApplyScheduleDatePickerDialogVisible,
  ] = React.useState<boolean>(false);

  const [configSettings, setConfigSettings] = React.useState<ConfigSetting[]>(
    [],
  );

  const [updatedSettings, setUpdatedSettings] = React.useState<
    EditConfigSetting[]
  >([]);

  const [assetsDataToExport, setAssetDataToExport] = React.useState<
    GridRowAssetTableInfo[]
  >([]);

  const [error, displayError] = React.useState(false);

  const [resetTrigger, setResetTrigger] = React.useState(0);

  const hasChanges = updatedSettings.some(row => row.IsDirty);

  useEffect((): ReturnType<React.EffectCallback> => {
    if (nodeConfigData.ConfigSettingList.length == 0) {
      getConfigSettingsByNodeUuid(dispatch, nodeConfigData.NodeInfo.Uuid);
    } else {
      setConfigSettings(nodeConfigData.ConfigSettingList);
    }
    return (): void => {};
  }, []);

  const configSettingsInfo: ConfigSetting[] = useSelector(
    (state: ApplicationState) => state.configSettingsReducer.configSettingsInfo,
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const editConfigStatus: boolean | undefined = useSelector(
    (state: ApplicationState) => state.configSettingsReducer.editConfigStatus,
  );

  const fetchError: FetchError = useSelector(
    (state: ApplicationState) => state.configSettingsReducer.fetchError,
  );

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const status = GetString('STATUS');

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    let timeoutId: any;

    if (editConfigStatus) {
      timeoutId = setTimeout(() => {
        clearEditConfigSettings(dispatch);
      }, 3000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [editConfigStatus]);

  const clearFetchError = () => {
    displayError(false);
    resetFetchError(dispatch);
  };

  useEffect(() => {
    let timeoutId: any;
    if (fetchError.StatusCode > 0 && fetchError.Message != '') {
      displayError(true);
      timeoutId = setTimeout(() => {
        clearFetchError();
      }, 3000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchError]);

  useEffect((): ReturnType<React.EffectCallback> => {
    if (nodeConfigData.ConfigSettingList.length == 0)
      setConfigSettings(configSettingsInfo);
    return (): void => {};
  }, [configSettingsInfo]);

  const getCSVFileName = () => {
    return `configSettings_export_${nodeConfigData.NodeInfo.SiteNumber}_${
      nodeConfigData.NodeInfo.LaneNumber
    }_${dateFormat(currentLocale.language)}`;
  };

  const exportToCSV = () => {
    if (nodeConfigData != undefined) {
      let nodeInfo = nodeConfigData;
      nodeInfo.ConfigSettingList = configSettings;

      let gridRowAssetInfo: GridRowAssetTableInfo[] =
        flattenNodeConfigData(nodeInfo);

      setAssetDataToExport(gridRowAssetInfo);
    }
  };

  useEffect(() => {
    if (assetsDataToExport.length > 0 && csvLinkRef?.current)
      csvLinkRef.current.link.click();
  }, [assetsDataToExport]);

  const processRequestCommand = (
    scheduledDateTime: string,
    applyNow: boolean,
  ) => {
    let selectedConfigSettings: ConfigSetting[] = [];

    updatedSettings.forEach(item => {
      if (item.IsDirty) {
        item.Setting.Value = item.NewValue;
        selectedConfigSettings.push(item.Setting);
      }
    });

    let updateConfigSetting: UpdateConfigSetting = {
      NodeUuids: [nodeConfigData.NodeInfo.Uuid],
      Settings: selectedConfigSettings,
      ScheduledDateTime: scheduledDateTime,
      ApplyNow: applyNow,
    };

    editConfigSettings(dispatch, configSettingType, updateConfigSetting);
    setResetTrigger(prev => prev + 1);
    setUpdatedSettings([]);
    closePage();
  };

  return (
    <React.Fragment>
      <CSVLink
        data={assetsDataToExport}
        ref={csvLinkRef}
        filename={getCSVFileName() + '.csv'}
      />
      <Grid
        container
        spacing={2}
        className={clsx(classes.gridAlign, classes.margin)}
      >
        <Grid item md={3} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('STORE')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {nodeConfigData.NodeInfo.SiteNumber}
          </Typography>
        </Grid>
        <Grid item md={3} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('DIVISION')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {nodeConfigData.NodeInfo.Division}
          </Typography>
        </Grid>
        <Grid item md={3} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('CLUSTER')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {nodeConfigData.NodeInfo.NodeGroup}
          </Typography>
        </Grid>
        <Grid item md={3} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('STATION_TYPE')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {nodeConfigData.NodeInfo.NodeType}
          </Typography>
        </Grid>
        <Grid item md={3} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('LANE')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {nodeConfigData.NodeInfo.LaneNumber}
          </Typography>
        </Grid>
        <Grid item md={3} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('NODE_ID')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {nodeConfigData.NodeInfo.NodeID}
          </Typography>
        </Grid>
        <Grid item md={6} xs={12}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('LAST_HEARTBEAT_DATETIME')}
          </Typography>
          <Typography
            variant="body1"
            className={clsx(classes.dialogLabel, classes.dialogTextBold)}
            gutterBottom
          >
            {getLocalDateTime(new Date(nodeConfigData.NodeInfo.LastHeartbeat))}
          </Typography>
        </Grid>
        <Grid item md={12} xs={12} style={{ height: '600px' }}>
          <Typography
            variant="body1"
            className={classes.dialogLabel}
            gutterBottom
          >
            {GetString('CONFIGURATION')}
          </Typography>
          {configSettingType === ApiEnums.ConfigSettingsType.JsonSetting ? (
            <JsonNodeConfigDetailComponent
              uuid={nodeConfigData.NodeInfo.Uuid}
              configSettings={configSettings}
              onSettingsChange={setUpdatedSettings}
              onRegisterOpenBBox={fn => {
                openBBoxRef.current = fn;
              }}
              resetTrigger={resetTrigger}
            />
          ) : (
            <NodeConfigDetailComponent configSettings={configSettings} />
          )}
        </Grid>
      </Grid>
      <Divider />

      <Box className={clsx(classes.spreadBox, classes.box)}>
        <Button
          themeColor={'primary'}
          type="submit"
          className={classes.dialogAction}
          onClick={closePage}
        >
          {GetString('BACK')}
        </Button>
        <Button
          themeColor={'primary'}
          type="submit"
          className={classes.dialogAction}
          onClick={exportToCSV}
        >
          {GetString('EXPORT_TO_CSV')}
        </Button>

        {configSettingType === ApiEnums.ConfigSettingsType.JsonSetting && (
          <>
            <Button
              themeColor="primary"
              className={classes.dialogAction}
              onClick={() => openBBoxRef.current?.()}
            >
              {GetString('EDIT_BOUNDING_BOXES')}
            </Button>
            <Button
              themeColor={'primary'}
              className={classes.dialogAction}
              onClick={() =>
                processRequestCommand(new Date().toISOString(), true)
              }
              disabled={!hasChanges}
            >
              <StringResourceManager stringKey={'APPLY_NOW'} />
            </Button>

            <Button
              themeColor={'primary'}
              className={classes.dialogAction}
              onClick={() => setIsApplyScheduleDatePickerDialogVisible(true)}
              disabled={!hasChanges}
            >
              <StringResourceManager stringKey={'APPLY_SCHEDULE'} />
            </Button>
          </>
        )}
      </Box>
      {isApplyScheduleDatePickerDialogVisible && (
        <ApplyScheduleDatePickerDialog
          processRequestCommand={processRequestCommand}
          handleCloseDialog={(e: boolean) =>
            setIsApplyScheduleDatePickerDialogVisible(e)
          }
        />
      )}
      <Fade>
        <NotificationGroup className={classes.bottomRight}>
          {editConfigStatus && (
            <Notification
              className={classes.send}
              type={{
                style: 'success',
                icon: true,
              }}
              closable={true}
              onClose={() => clearEditConfigSettings(dispatch)}
            >
              <StringResourceManager stringKey="COMMAND_SENT_SUCCESSFULLY" />
            </Notification>
          )}
          {error && (
            <Notification
              className={classes.error}
              type={{
                style: 'error',
                icon: true,
              }}
              closable={true}
              onClose={() => clearFetchError()}
            >
              <StringResourceManager stringKey={fetchError.Message} />
              {`(${status}: ${fetchError.StatusCode})`}
            </Notification>
          )}
        </NotificationGroup>
      </Fade>
    </React.Fragment>
  );
};

export default NodeAssetsConfigForm;
